
import { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light" | "system";
type ThemeColor = "arqum";
type Density = "compact" | "comfortable";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultColor?: ThemeColor;
  defaultDensity?: Density;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  resolvedTheme: "dark" | "light";
  color: ThemeColor;
  density: Density;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setColor: (color: ThemeColor) => void;
  setDensity: (density: Density) => void;
};

const initialState: ThemeProviderState = {
  theme: "system",
  resolvedTheme: "dark",
  color: "arqum",
  density: "comfortable",
  setTheme: () => null,
  toggleTheme: () => null,
  setColor: () => null,
  setDensity: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "system",
  defaultColor = "arqum",
  defaultDensity = "comfortable",
  storageKey = "arqum-ui-theme",
}: ThemeProviderProps) {
  const normalizeColor = (value: string | null): ThemeColor =>
    value === "arqum" ? value : defaultColor;

  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(`${storageKey}-mode`) as Theme) || defaultTheme
  );
  const [resolvedTheme, setResolvedTheme] = useState<"dark" | "light">("dark");
  
  const [color, setColor] = useState<ThemeColor>(
    () => normalizeColor(localStorage.getItem(`${storageKey}-color`))
  );

  const [density, setDensity] = useState<Density>(
    () => (localStorage.getItem(`${storageKey}-density`) as Density) || defaultDensity
  );

  useEffect(() => {
    const root = window.document.documentElement;

    // Handle Theme Mode (Dark/Light)
    root.classList.remove("light", "dark");

    const appliedTheme = theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light"
      : theme;

    root.classList.add(appliedTheme);
    setResolvedTheme(appliedTheme);
  }, [theme]);

  useEffect(() => {
    const body = window.document.body;
    
    // Handle Color Palette
    // Remove all existing theme-* classes
    const themeClasses = Array.from(body.classList).filter(c => c.startsWith('theme-'));
    body.classList.remove(...themeClasses);
    
    // Add new theme class
    body.classList.add(`theme-${color}`);
  }, [color]);

  useEffect(() => {
    const body = window.document.body;
    
    // Handle Density
    body.setAttribute("data-density", density);
  }, [density]);

  const value = {
    theme,
    resolvedTheme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(`${storageKey}-mode`, theme);
      setTheme(theme);
    },
    toggleTheme: () => {
      const nextTheme = resolvedTheme === "dark" ? "light" : "dark";
      localStorage.setItem(`${storageKey}-mode`, nextTheme);
      setTheme(nextTheme);
    },
    color,
    setColor: (color: ThemeColor) => {
      localStorage.setItem(`${storageKey}-color`, color);
      setColor(color);
    },
    density,
    setDensity: (density: Density) => {
      localStorage.setItem(`${storageKey}-density`, density);
      setDensity(density);
    },
  };

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};
